/**
 * Widget Service
 * Main widget class that handles initialization, container creation, and lifecycle
 */

import "@/i18n"; // initialize i18next before any component renders
// Stores before any socket handler: the store module wires up
// store-subscriptions, which imports the stream handlers — evaluated
// handler-first, a subclass would meet `StreamHandler` uninitialized.
import "@/stores";
import { initWidgetOpen } from "@/hooks/widget";
import { CustomButtonService } from "./custom-button.service";
import {
  createShadowContainer,
  injectStylesIntoShadow,
  isShadowDOMSupported,
} from "@/utils/shadow-dom";
import type { ComponentType } from "react";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";
import {
  WebSocketService,
  actionHandler,
  gamesListHandler,
  gameCampaignsHandler,
  authHandler,
  configHandler,
  tournamentLeaderboardsHandler,
  tournamentProgressHandler,
  campaignsHandler,
  campaignProgressHandler,
  prizeDropPrizesHandler,
  missionProgressHandler,
  sprintMissionPoolHandler,
  jackpotProgressHandler,
  raffleProgressHandler,
  notificationsHandler,
  heartbeatHandler,
} from "./websocket";
import type { WidgetVariant } from "@/types";
import { WidgetVariantProvider } from "@/contexts/widget-variant-context";
import { getDefaultContainerId } from "@/constants/widget-events";
import { websocketConfig } from "@/config/websocket.config";
import { startCredentialsWatcher } from "./credentials-watcher";
import { startPrefsWatcher } from "./prefs-watcher";
import { getUserIdCookie } from "@/utils";
import { useAppStore } from "@/stores";

const WIDGET_NAMESPACE = "unibo-widget";

export interface WidgetConfig {
  variant?: WidgetVariant;
  useShadowDOM?: boolean;
  shadowMode?: "open" | "closed";
  containerId?: string;
  autoInit?: boolean;
  defaultOpen?: boolean;
  zIndex?: number;
  persistState?: boolean;
  darkMode?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  websocketUrl?: string;
  // When set, the widget host attaches inside this element instead of
  // document.body and is bounded to it (used for in-game / gameframe mode).
  targetContainer?: HTMLElement;
  // The tree mounted into the container. Passed in by the entry rather than
  // imported here, so each target bundles only its own UI — the overlay never
  // pulls in the widgets' router.
  rootComponent?: ComponentType;
}

interface ContainerState {
  root: Root | null;
  shadowContainer: {
    host: HTMLElement;
    shadowRoot: ShadowRoot;
    reactContainer: HTMLElement;
  } | null;
  regularContainer: HTMLElement | null;
}

export class WidgetService {
  private containerState: ContainerState | null = null;
  private buttonService: CustomButtonService | null = null;
  private wsService: WebSocketService | null = null;
  private wsInitialized = false;
  private credentialsWatcherCleanup: (() => void) | null = null;
  private prefsWatcherCleanup: (() => void) | null = null;
  private wsEventHandlers: {
    connect?: () => void;
    disconnect?: (code?: number, reason?: string) => void;
    error?: (error: Error) => void;
  } = {};
  private config: WidgetConfig;

  constructor(config: WidgetConfig = {}) {
    this.config = {
      variant: "on-site",
      useShadowDOM: undefined,
      shadowMode: "open",
      autoInit: true,
      defaultOpen: false,
      zIndex: 9999,
      darkMode: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    // Initialize widget state
    initWidgetOpen(
      this.config.defaultOpen ?? false,
      this.config.persistState ?? false
    );

    // Before the socket: assembly reads it when the first `campaigns` push lands.
    useAppStore.getState().setVariant(this.config.variant ?? "on-site");

    // The overlay is docked, not launched: the host page has no trigger button.
    if (this.config.variant !== "overlay") {
      this.buttonService = new CustomButtonService(this.config.variant);
      this.buttonService.initialize();
    }

    await this.initializeWebSocket();

    // Create widget container (skip if autoInit is false, e.g., in dev mode,
    // or when the entry mounts its own tree, as the overlay element does)
    const { rootComponent } = this.config;
    if (
      this.config.autoInit !== false &&
      rootComponent &&
      !this.containerState
    ) {
      this.containerState = await this.createContainer(rootComponent);
    }
  }

  private async initializeWebSocket(): Promise<void> {
    // Prevent multiple initializations
    if (this.wsInitialized) {
      return;
    }

    try {
      this.wsService = WebSocketService.getInstance();

      // Register handler singletons (only once)
      this.wsService.registerHandler("set_player", authHandler);
      this.wsService.registerHandler("authenticate", authHandler);
      this.wsService.registerHandler("configure", configHandler);
      this.wsService.registerHandler(
        "subscribe:tournament_leaderboards",
        tournamentLeaderboardsHandler
      );
      this.wsService.registerHandler(
        "update:tournament_leaderboards",
        tournamentLeaderboardsHandler
      );
      this.wsService.registerHandler(
        "subscribe:tournament_progress",
        tournamentProgressHandler
      );
      this.wsService.registerHandler(
        "update:tournament_progress",
        tournamentProgressHandler
      );
      // Type-agnostic campaign streams + the prize-drop volatile stream.
      this.wsService.registerHandler("subscribe:campaigns", campaignsHandler);
      this.wsService.registerHandler("update:campaigns", campaignsHandler);
      this.wsService.registerHandler(
        "subscribe:campaign_progress",
        campaignProgressHandler
      );
      this.wsService.registerHandler(
        "update:campaign_progress",
        campaignProgressHandler
      );
      this.wsService.registerHandler(
        "subscribe:prize_drop_prizes",
        prizeDropPrizesHandler
      );
      this.wsService.registerHandler(
        "update:prize_drop_prizes",
        prizeDropPrizesHandler
      );
      this.wsService.registerHandler(
        "subscribe:mission_progress",
        missionProgressHandler
      );
      this.wsService.registerHandler(
        "update:mission_progress",
        missionProgressHandler
      );
      // Sprint Mission pool counter. Without these two registrations the stream is subscribed
      // (store-subscriptions) but every page is dropped on arrival, and since the stream has no
      // periodic push the counter never loads (#3958).
      this.wsService.registerHandler(
        "subscribe:sprint_mission_pool",
        sprintMissionPoolHandler
      );
      this.wsService.registerHandler(
        "update:sprint_mission_pool",
        sprintMissionPoolHandler
      );
      // Jackpot pots — same shape as the pool counter: subscribed by store-subscriptions, but
      // the pages only land if the two message types are routed here.
      this.wsService.registerHandler(
        "subscribe:jackpot_progress",
        jackpotProgressHandler
      );
      this.wsService.registerHandler(
        "update:jackpot_progress",
        jackpotProgressHandler
      );
      // Raffle tickets — per-player, same registration requirement as the other volatile streams.
      this.wsService.registerHandler(
        "subscribe:raffle_progress",
        raffleProgressHandler
      );
      this.wsService.registerHandler(
        "update:raffle_progress",
        raffleProgressHandler
      );
      // Player notifications (prize wins queue for reveal; the rest are acked
      // on receipt). The handler also consumes our ack responses.
      this.wsService.registerHandler(
        "subscribe:notifications",
        notificationsHandler
      );
      this.wsService.registerHandler(
        "update:notifications",
        notificationsHandler
      );
      this.wsService.registerHandler("ack:notifications", notificationsHandler);
      this.wsService.registerHandler("action:opt_in", actionHandler);
      this.wsService.registerHandler("action:opt_out", actionHandler);
      this.wsService.registerHandler("action:list_games", gamesListHandler);
      this.wsService.registerHandler(
        "action:game_campaigns",
        gameCampaignsHandler
      );
      this.wsService.registerHandler("ping", heartbeatHandler);

      // Get WebSocket URL
      const wsUrl = this.config.websocketUrl || websocketConfig.url;

      // Remove old event listeners if they exist
      if (this.wsEventHandlers.connect) {
        this.wsService.off("connect", this.wsEventHandlers.connect);
      }
      if (this.wsEventHandlers.disconnect) {
        this.wsService.off("disconnect", this.wsEventHandlers.disconnect);
      }
      if (this.wsEventHandlers.error) {
        this.wsService.off("error", this.wsEventHandlers.error);
      }

      // Setup connection event listeners (store references for cleanup)
      this.wsEventHandlers.connect = async () => {
        console.log(`[${WIDGET_NAMESPACE}] WebSocket connected`);
        // Auth is a per-connection handshake — the backend binds the player to THIS
        // socket, so we must (re)authenticate on every (re)connection, not only when
        // creds first appear. Otherwise a reconnect leaves the new socket
        // unauthenticated and the backend never sends it updates. authenticate()
        // safely no-ops when the player has no credentials (logged out).
        if (getUserIdCookie()) {
          await this.authenticateWs();
        }
      };

      this.wsEventHandlers.disconnect = (code, reason) => {
        console.log(
          `[${WIDGET_NAMESPACE}] WebSocket disconnected: ${code} - ${reason || "No reason"}`
        );
        // Keep the player authenticated and the last data on screen during a
        // transient drop; only reset the per-connection flags so the reconnect
        // re-runs the server-side bring-up (auth → configure → subscribe):
        //  - clear the in-flight auth guard so re-auth can proceed,
        //  - drop the `subscribed` flags WITHOUT wiping the displayed tournaments
        //    (setTournament*Subscribed keeps the data; clear*() would not),
        //  - null the config so the fresh configure response is a change and
        //    re-triggers the subscribe.
        authHandler.authenticating = false;
        // Drop any in-flight opt-ins — their acks may not survive the drop, and
        // a stale FIFO entry would misattribute every later opt-in's ack. The
        // reconnect's campaign_progress push reconciles the true joined state.
        actionHandler.reset();
        // In-flight games pages won't be answered either: fail them so the dialog can retry.
        gamesListHandler.reset();
        // Nor will the overlay's game question: let it be asked again on reconnect.
        gameCampaignsHandler.reset();
        const store = useAppStore.getState();
        store.setTournamentLeaderboardsSubscribed(false);
        store.setTournamentProgressSubscribed(false);
        store.setCampaignsSubscribed(false);
        store.setCampaignProgressSubscribed(false);
        store.setPrizeDropPrizesSubscribed(false);
        // Every stream, not only the original ones: a flag left `true` here makes the
        // handler's "already subscribed" guard skip the re-subscribe after the reconnect,
        // and that stream silently freezes on its pre-drop data (seen on a server restart:
        // mission progress and the Sprint Mission pool never moved again).
        store.setMissionProgressSubscribed(false);
        store.setSprintMissionPoolSubscribed(false);
        store.setNotificationsSubscribed(false);
        store.setConfig(null, null);
      };

      this.wsEventHandlers.error = (error) => {
        console.error(`[${WIDGET_NAMESPACE}] WebSocket error:`, error);
      };

      this.wsService.on("connect", this.wsEventHandlers.connect);
      this.wsService.on("disconnect", this.wsEventHandlers.disconnect);
      this.wsService.on("error", this.wsEventHandlers.error);

      // Mark as initialized before connecting
      this.wsInitialized = true;
      this.wsService.connect(wsUrl);

      this.startCredentialsWatcher();
      this.startPrefsWatcher();
    } catch (error) {
      console.error(
        `[${WIDGET_NAMESPACE}] Failed to initialize WebSocket:`,
        error
      );
      this.wsInitialized = false; // Reset on error
    }
  }

  private async authenticateWs(): Promise<void> {
    if (authHandler.authenticating) {
      return;
    }

    authHandler.authenticate();
  }

  private startCredentialsWatcher(): void {
    if (this.credentialsWatcherCleanup) return;

    this.credentialsWatcherCleanup = startCredentialsWatcher((event) => {
      switch (event.type) {
        case "appeared":
          this.tryAuthenticate();
          break;
        case "changed":
          authHandler.logout();
          this.tryAuthenticate();
          break;
        case "removed":
          authHandler.logout();
          break;
      }
    });
  }

  private startPrefsWatcher(): void {
    if (this.prefsWatcherCleanup) return;

    this.prefsWatcherCleanup = startPrefsWatcher(() => {
      // Re-send `configure` over the already-open socket instead of
      // reconnecting (unlike the legacy overlay, which reconnects wholesale
      // on a language change) — the backend applies it in place and the
      // response updates the store, which re-syncs i18next/re-subscribes.
      if (
        this.wsService?.getStatus() === "connected" &&
        useAppStore.getState().authenticated
      ) {
        configHandler.sendConfiguration();
      }
    });
  }

  private tryAuthenticate(): void {
    if (!this.wsService) {
      return;
    }
    if (this.wsService.getStatus() === "connected") {
      void this.authenticateWs();
    } else {
      // Connect handler will pick this up once the socket opens.
    }
  }

  /**
   * Create widget container (Shadow DOM or Regular DOM)
   */
  private async createContainer(
    RootComponent: ComponentType
  ): Promise<ContainerState> {
    const state: ContainerState = {
      root: null,
      shadowContainer: null,
      regularContainer: null,
    };

    const variant = this.config.variant ?? "on-site";
    const containerId =
      this.config.containerId ?? getDefaultContainerId(variant);
    const useShadowDOM =
      this.config.useShadowDOM ?? (isShadowDOMSupported() ? true : false);

    if (useShadowDOM && isShadowDOMSupported()) {
      // Shadow DOM mode
      state.shadowContainer = createShadowContainer(
        containerId,
        this.config.zIndex ?? 9999,
        {
          mode: this.config.shadowMode ?? "open",
          delegatesFocus: true,
          parent: this.config.targetContainer,
        }
      );

      if (this.config.darkMode !== false) {
        state.shadowContainer.host.classList.add("dark");
      }

      // Inject CSS
      try {
        const cssModule = await import("../index.css?inline");
        const cssContent =
          typeof cssModule.default === "string"
            ? cssModule.default
            : String(cssModule.default);

        await injectStylesIntoShadow(
          state.shadowContainer.shadowRoot,
          cssContent
        );
      } catch (error) {
        console.error(
          `[${WIDGET_NAMESPACE}] Failed to inject CSS into Shadow DOM:`,
          error
        );
      }

      state.root = createRoot(state.shadowContainer.reactContainer);
      state.root.render(
        <WidgetVariantProvider variant={variant}>
          <RootComponent />
        </WidgetVariantProvider>
      );

      console.log(`[${WIDGET_NAMESPACE}] Widget initialized with Shadow DOM`);
    } else {
      // Regular DOM mode
      let container = document.getElementById(containerId);

      if (!container) {
        const parent = this.config.targetContainer ?? document.body;
        const positionStyle = this.config.targetContainer
          ? "absolute"
          : "fixed";
        container = document.createElement("div");
        container.id = containerId;
        container.setAttribute("data-widget", WIDGET_NAMESPACE);
        container.style.cssText = `
          position: ${positionStyle};
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: ${this.config.zIndex ?? 9999};
          isolation: isolate;
        `;
        parent.appendChild(container);
      }

      if (this.config.darkMode !== false) {
        container.classList.add("dark");
      }

      state.regularContainer = container;

      state.root = createRoot(container);
      state.root.render(
        <WidgetVariantProvider variant={variant}>
          <RootComponent />
        </WidgetVariantProvider>
      );

      console.log(
        `[${WIDGET_NAMESPACE}] Widget initialized${!isShadowDOMSupported() ? " (Shadow DOM not supported, using regular DOM)" : ""}`
      );
    }

    return state;
  }

  /**
   * Destroy widget and cleanup
   */
  destroy(): void {
    // Cleanup WebSocket event listeners
    if (this.wsService) {
      if (this.wsEventHandlers.connect) {
        this.wsService.off("connect", this.wsEventHandlers.connect);
      }
      if (this.wsEventHandlers.disconnect) {
        this.wsService.off("disconnect", this.wsEventHandlers.disconnect);
      }
      if (this.wsEventHandlers.error) {
        this.wsService.off("error", this.wsEventHandlers.error);
      }
      this.wsService.disconnect();
      this.wsService = null;
    }
    this.wsEventHandlers = {};
    this.wsInitialized = false;

    // A destroyed service can be initialized again, possibly for another
    // player (the overlay's unmount → init). Drop identity and config so the
    // next connection runs the full auth → configure → subscribe bring-up;
    // left set, the subscribe trigger sees no change and the streams stay stale.
    authHandler.logout();
    actionHandler.reset();
    gamesListHandler.reset();
    useAppStore.getState().setConfig(null, null);

    if (this.credentialsWatcherCleanup) {
      this.credentialsWatcherCleanup();
      this.credentialsWatcherCleanup = null;
    }

    if (this.prefsWatcherCleanup) {
      this.prefsWatcherCleanup();
      this.prefsWatcherCleanup = null;
    }

    if (this.buttonService) {
      this.buttonService.destroy();
      this.buttonService = null;
    }

    if (this.containerState) {
      if (this.containerState.root) {
        this.containerState.root.unmount();
        this.containerState.root = null;
      }

      if (
        this.containerState.shadowContainer?.host &&
        this.containerState.shadowContainer.host.parentNode
      ) {
        this.containerState.shadowContainer.host.parentNode.removeChild(
          this.containerState.shadowContainer.host
        );
        this.containerState.shadowContainer = null;
      } else if (
        this.containerState.regularContainer &&
        this.containerState.regularContainer.parentNode
      ) {
        this.containerState.regularContainer.parentNode.removeChild(
          this.containerState.regularContainer
        );
        this.containerState.regularContainer = null;
      }

      this.containerState = null;
    }

    console.log(`[${WIDGET_NAMESPACE}] Widget destroyed`);
  }
}
